/**
 * タブ
 */
export class Tab {
  /**
   * コンストラクタ
   * @param target 対象の要素セレクタ
   * @param setting オプション
   */
  constructor(target: string, setting: TabOption = {
    directLink: true,
    hold: false,
    history: false,
    tabName: 'fn-tab__list',
    panelName: 'fn-tab__panel'
  }) {
    $(target).each(function(index: number, element: Element): void {
      const $this: JQuery = $(element);
      $this.data('tab', new TabObject($this, index, setting));
    });
  }
}

/**
 * オプション
 */
export interface TabOption {
  /**
   * タブパネル内のIDへアンカージャンプした際のパネル切り替え
   */
  directLink: boolean;

  /**
   * 表示中のタブの情報のセッション中の保持
   */
  hold: boolean;

  /**
   * ブラウザバックでのパネルの切り替え
   */
  history: boolean;

  /**
   * タブリストのクラス名
   */
  tabName: string;

  /**
   * タブパネル（コンテンツ）のクラス名
   */
  panelName: string;
}

/**
 * 実行クラス
 */
class TabObject {
  /**
   * 要素リスト
   */
  private readonly el: any;

  /**
   * 設定オプション
   */
  private readonly state: any;

  /**
   * オプション
   */
  private readonly setting: TabOption;

  /**
   * コンストラクタ
   * @param element 対象の要素オブジェクト
   * @param index タブのインデックス
   * @param setting オプション
   */
  constructor(element: JQuery, index: number, setting: TabOption) {
    this.el = {};
    this.state = {};
    this.setting = setting;
    this.el.$root = element;
    this.el.$win = $(window);
    this.el.$tabList = this.el.$root.find('.' + setting.tabName);
    this.el.$tabItem = this.el.$tabList.children();
    this.el.$tabAnc = this.el.$tabItem.children();
    this.el.$tabPanel = this.el.$root.find('.' + setting.panelName);
    this.state.rootNumber = index + 1;
    this.state.length = this.el.$tabPanel.length;
    this.state.panelIds = [];
    this.init();
  }

  /**
   * 初期ID取得関数
   * @returns 表示するID
   */
  private getDefaultId(): string | null {
    let id: string | null = this.state.panelIds[0];

    // sessionStorageから取得
    if (this.setting.hold) {
      id = sessionStorage.getItem('standard-tab') ? sessionStorage.getItem('standard-tab') : id;
    }

    // ハッシュから取得
    id = (this.getHash()) ? this.getHash() : id;
    return id;
  }

  /**
   * ハッシュIDの取得
   * @return 表示するID
   */
  private getHash(): string | null {
    const hash: string = location.hash.replace(/^#(.+?)$/g, '$1');

    // ハッシュがなければ終了
    if (! hash) {
      return null;
    }

    // タブパネルに該当しない場合
    if (this.state.panelIds.indexOf(hash) === -1) {
      // タブパネル内に該当する要素がなければ終了
      if (! this.el.$tabPanel.find('#' + hash).length) {
        return null;
      }

      if (! this.setting.directLink) {
        return null;
      }

      return this.el.$tabPanel.find('#' + hash).parents('.' + this.setting.panelName).attr('id');
    }

    return hash;
  }

  /**
   * 初期実行メソッド
   */
  private init(): void {
    // タブパネルのIDを取得
    for (let i: number = 0; i < this.state.length; i++) {
      this.state.panelIds.push(this.el.$tabAnc.eq(i).attr('href').replace(/^#(.+?)$/g, '$1'));
    }

    // 初期に表示するパネルを指定
    this.state.isShowId = this.getDefaultId();
    this.setAttr();
    this.setEvent();
    this.el.$tabItem.filter('.is-current').trigger('click');
  }

  /**
   * 属性の追加メソッド
   */
  private setAttr(): void {
    // role属性の付与
    this.el.$tabList.attr('role', 'tablist');
    this.el.$tabItem.attr('role', 'tab');
    this.el.$tabPanel.attr('role', 'tabpanel');

    // aria-属性などの付与
    for (let i: number = 0; i < this.state.length; i++) {
      const num: string = ('00' + this.state.rootNumber).slice(-2) + '-' + ('00' + (i + 1)).slice(-2);
      const panelId: string = this.el.$tabAnc.eq(i).attr('href').replace(/^#(.+?)$/g, '$1');

      // タブ
      this.el.$tabItem.eq(i).attr({
        'id': 'tabItem-' + num,
        'aria-controls': panelId,
        'aria-selected': 'false',
        'tabindex': '-1'
      });

      // パネル
      this.el.$tabPanel.eq(i).attr({
        'id': panelId,
        'aria-labelledby': 'tabItem-' + num,
        'aria-hidden': 'true'
      });
    }

    // タブのリンクからhref属性を除去
    this.el.$tabAnc.removeAttr('href');
  }

  /**
   * 表示の更新メソッド
   */
  private updateView(isFirst: boolean = false): void {
    // 現状の表示をリセット
    this.el.$tabItem
      .removeClass('is-current')
      .attr({
        'aria-selected': 'false',
        'tabindex': '-1'
      });
    this.el.$tabPanel
      .hide()
      .attr('aria-hidden', 'true');

    // 表示を更新する
    const $target: JQuery = this.el.$tabItem.filter(`[aria-controls="${this.state.isShowId}"]`)
      .addClass('is-current')
      .attr({
        'aria-selected': 'true',
        'tabindex': '0'
      });

    this.el.$tabPanel.filter('#' + this.state.isShowId)
      .show()
      .attr('aria-hidden', 'false');
  }

  /**
   * ステートのアップデートメソッド
   * @param id 表示するタブパネルのID
   */
  private updateState(id: string): void {
    this.state.isShowId = id;

    if (this.setting.hold) {
      sessionStorage.setItem('standard-tab', this.state.isShowId);
    }

    this.updateView();
  }

  /**
   * historyに追加メソッド
   * @param id historyに追加するID
   */
  private setHistory(id: string): void {
    if (this.setting.history) {
      history.pushState('standard-tab', '', '#' + id);
    }
  }

  /**
   * イベントのセットメソッド
   */
  private setEvent(): void {
    // タブアイテムのクリックで更新
    this.el.$tabItem.on('click', (event: JQueryEventObject): void => {
      const id: string = $(event.currentTarget).attr('aria-controls');
      this.updateState(id);
      this.setHistory(id);
    });

    // タブアイテムフォーカス中のキー操作
    this.el.$tabItem.on('keyup', (event: JQueryEventObject): void => {
      const $element: JQuery = $(event.target);
      let id: string = '';

      switch (event.keyCode) {
        // End
        case 35:
          id = this.el.$tabItem.eq(this.state.length - 1).attr('aria-controls');
          break;
        // Home
        case 36:
          id = this.el.$tabItem.eq(0).attr('aria-controls');
          break;
        // 左
        case 37:
          if ($element.index() === 0) {
            return;
          }
          id = $element.prev().attr('aria-controls');
          break;
        // 右
        case 39:
          if ($element.index() === this.state.length - 1) {
            return;
          }
          id = $element.next().attr('aria-controls');
          break;
        default:
          return;
      }

      this.updateState(id);
      this.setHistory(id);
    });

    // 戻る・進むイベントの監視
    if (this.setting.history) {
      this.el.$win.on('popstate', (event: JQueryEventObject): void => {
        const id: string = (this.getHash()) ? this.getHash() : this.el.$tabItem.eq(0).attr('aria-controls');
        this.updateState(id);
      });
    }
  }
}
